import React, { useState } from 'react';
import { Link,useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, Loader2, Shield, KeyRound, Fingerprint } from 'lucide-react';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role !== 'admin' && user.role !== 'employee') {
        setError('Access denied. This login is for admin and employees only.');
        setLoading(false);
        return;
      }
      localStorage.removeItem('redirect_after_login');
      navigate(user.role === 'admin' ? '/admin' : '/employee', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0A1E40 0%, #142d5e 50%, #0A1E40 100%)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        
        <div className="relative z-10 max-w-lg">
          {/* Logo */}
          {/* <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-[#FF5A36] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-2xl">E</span>
            </div>
            <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>EasyMyVisa</span>
          </div> */}

           <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">        
                  <img src="/images/logo1.png" alt="EasyMyVisa Logo" className=""  style={{ width: '250px', height: '80px' }} />
            </Link>

          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Staff<br />
            <span className="text-[#FF5A36]">Command Center</span>
          </h1>
          <p className="text-white/50 text-lg mb-12 leading-relaxed">
            Manage applications, process visas, and oversee the entire platform from one secure dashboard.
          </p>

          {/* Feature highlights */}
          <div className="space-y-5">
            {[
              { icon: Shield, label: 'Role-based access control' },
              { icon: KeyRound, label: 'Encrypted authentication' },
              { icon: Fingerprint, label: 'Session monitoring' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#FF5A36]" />
                </div>
                <span className="text-white/60 text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-10 h-10 bg-[#FF5A36] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">E</span>
            </div>
            <span className="text-white font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>EasyMyVisa</span>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 p-8 lg:p-10">
            {/* Header */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 bg-[#FF5A36]/10 text-[#FF5A36] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                <Shield className="w-3.5 h-3.5" /> Staff Only
              </div>
              <h2 className="text-2xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome back
              </h2>
              <p className="text-[#4A5568] text-sm mt-1">Sign in to access the admin dashboard</p>
            </div>

            {error && (
              <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <Label htmlFor="admin-email" className="text-[#0A1E40] font-medium text-sm">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input
                    id="admin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@easymyvisa.com"
                    className="pl-11 h-12 bg-[#F5F0E6] border-transparent focus:border-[#FF5A36] focus:bg-white transition-all"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="admin-password" className="text-[#0A1E40] font-medium text-sm">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pl-11 pr-11 h-12 bg-[#F5F0E6] border-transparent focus:border-[#FF5A36] focus:bg-white transition-all"
                    required
                    data-testid="admin-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A1E40]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white h-12 rounded-xl font-semibold text-base"
                data-testid="admin-login-btn"
              >
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Authenticating...</> : 'Sign In'}
              </Button>
            </form>
          </div>

          <p className="text-center mt-8 text-white/20 text-xs">
            Authorized Personnel Only · EasyMyVisa
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
